'use client';

import Image from 'next/image';
import { 
  Sparkles, ArrowRight, MessageSquare, ShoppingCart, 
  DollarSign, Store, Users, Zap, Warehouse, Wrench 
} from 'lucide-react';
import styles from './HeroSection.module.css';

export function HeroSection() {
  return (
    <section className={styles.hero}>
      <div className={styles.container}>
        
        {/* ==============================================
            COLUNA ESQUERDA: TEXTO E CTA
            ============================================== */}
        <div className={styles.textContent}>
          <div className={styles.heroBadge}>
            <span>Novo</span>
            <Sparkles size={14} color="#ea580c"/>
            O Sistema que a sua concorrência não quer que você descubra.
          </div>

          <h1 className={styles.heroTitle}>
            Gestão Empresarial <br />
            <span className={styles.textGradient}>Definitiva e Automática.</span>
          </h1>
          
          <p className={styles.heroDescription}>
            Esqueça as dezenas de mensalidades. Tenha Vendas, PDV, Financeiro, CRM e Atendimento de IA numa única plataforma desenhada para o lucro.
          </p>
          
          <div className={styles.heroActions}>
            <a href="#planos" className={styles.btnPrimary}>
              Criar Conta Grátis
              <ArrowRight size={18} />
            </a>

            <a href="https://api.whatsapp.com/send/?phone=5511983493522" target="_blank" rel="noopener noreferrer" className={styles.btnSecondary}>
              Falar com Consultor
            </a>
          </div>

          <div className={styles.trustBadges}>
            <p>Plataforma Oficial & Parceira Certificada</p>
            <div className={styles.badgesRow}>
              <Image src="/assets/images/googlepartner.png" alt="Google Partner" width={140} height={70} style={{ objectFit: 'contain', opacity: 0.8 }} priority/>
              <div className={styles.separator}></div>
              <Image src="/assets/images/metapartner2.png" alt="Meta Business Partner" width={130} height={35} style={{ objectFit: 'contain', opacity: 0.8 }} priority/>
            </div>
          </div>
        </div>

        {/* ==============================================
            COLUNA DIREITA: RADIAL DIAGRAM COM FLUXO DE DADOS
            ============================================== */}
        <div className={styles.visualContent}>
          <div className={styles.radialContainer}>
            
            <svg className={styles.connectingLines} viewBox="0 0 500 500">
              {/* Filtro de brilho para os pacotes de dados */}
              <defs>
                <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
                  <feGaussianBlur stdDeviation="3" result="blur" />
                  <feMerge>
                    <feMergeNode in="blur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>

              {/* Linhas Guias Tracejadas (Fundo) */}
              <line x1="250" y1="0" x2="250" y2="250" className={styles.line} />
              <line x1="425" y1="75" x2="250" y2="250" className={styles.line} />
              <line x1="500" y1="250" x2="250" y2="250" className={styles.line} />
              <line x1="425" y1="425" x2="250" y2="250" className={styles.line} />
              <line x1="250" y1="500" x2="250" y2="250" className={styles.line} />
              <line x1="75" y1="425" x2="250" y2="250" className={styles.line} />
              <line x1="0" y1="250" x2="250" y2="250" className={styles.line} />
              <line x1="75" y1="75" x2="250" y2="250" className={styles.line} />

              {/* --- BOLINHAS (DADOS) VIAJANDO PARA O CENTRO --- */}
              
              {/* Do Topo para o Centro */}
              <circle r="4.5" fill="#ea580c" filter="url(#glow)">
                <animate attributeName="cx" values="250; 250" dur="2.5s" repeatCount="indefinite" begin="0s" />
                <animate attributeName="cy" values="0; 250" dur="2.5s" repeatCount="indefinite" begin="0s" />
                <animate attributeName="opacity" values="1; 1; 0" keyTimes="0; 0.7; 1" dur="2.5s" repeatCount="indefinite" begin="0s" />
              </circle>

              {/* Do Topo-Direita para o Centro */}
              <circle r="4.5" fill="#3b82f6" filter="url(#glow)">
                <animate attributeName="cx" values="425; 250" dur="3s" repeatCount="indefinite" begin="0.8s" />
                <animate attributeName="cy" values="75; 250" dur="3s" repeatCount="indefinite" begin="0.8s" />
                <animate attributeName="opacity" values="1; 1; 0" keyTimes="0; 0.7; 1" dur="3s" repeatCount="indefinite" begin="0.8s" />
              </circle>

              {/* Da Direita para o Centro */}
              <circle r="4.5" fill="#10b981" filter="url(#glow)">
                <animate attributeName="cx" values="500; 250" dur="2.2s" repeatCount="indefinite" begin="0.3s" />
                <animate attributeName="cy" values="250; 250" dur="2.2s" repeatCount="indefinite" begin="0.3s" />
                <animate attributeName="opacity" values="1; 1; 0" keyTimes="0; 0.7; 1" dur="2.2s" repeatCount="indefinite" begin="0.3s" />
              </circle>

              {/* Do Baixo-Direita para o Centro */}
              <circle r="4.5" fill="#8b5cf6" filter="url(#glow)">
                <animate attributeName="cx" values="425; 250" dur="2.8s" repeatCount="indefinite" begin="1.5s" />
                <animate attributeName="cy" values="425; 250" dur="2.8s" repeatCount="indefinite" begin="1.5s" />
                <animate attributeName="opacity" values="1; 1; 0" keyTimes="0; 0.7; 1" dur="2.8s" repeatCount="indefinite" begin="1.5s" />
              </circle>

              {/* Do Baixo para o Centro */}
              <circle r="4.5" fill="#ea580c" filter="url(#glow)">
                <animate attributeName="cx" values="250; 250" dur="3.2s" repeatCount="indefinite" begin="0.6s" />
                <animate attributeName="cy" values="500; 250" dur="3.2s" repeatCount="indefinite" begin="0.6s" />
                <animate attributeName="opacity" values="1; 1; 0" keyTimes="0; 0.7; 1" dur="3.2s" repeatCount="indefinite" begin="0.6s" />
              </circle>

              {/* Do Baixo-Esquerda para o Centro */}
              <circle r="4.5" fill="#3b82f6" filter="url(#glow)">
                <animate attributeName="cx" values="75; 250" dur="2.4s" repeatCount="indefinite" begin="1.1s" />
                <animate attributeName="cy" values="425; 250" dur="2.4s" repeatCount="indefinite" begin="1.1s" />
                <animate attributeName="opacity" values="1; 1; 0" keyTimes="0; 0.7; 1" dur="2.4s" repeatCount="indefinite" begin="1.1s" />
              </circle>

              {/* Da Esquerda para o Centro */}
              <circle r="4.5" fill="#10b981" filter="url(#glow)">
                <animate attributeName="cx" values="0; 250" dur="2.7s" repeatCount="indefinite" begin="0.2s" />
                <animate attributeName="cy" values="250; 250" dur="2.7s" repeatCount="indefinite" begin="0.2s" />
                <animate attributeName="opacity" values="1; 1; 0" keyTimes="0; 0.7; 1" dur="2.7s" repeatCount="indefinite" begin="0.2s" />
              </circle>

              {/* Do Topo-Esquerda para o Centro */}
              <circle r="4.5" fill="#8b5cf6" filter="url(#glow)">
                <animate attributeName="cx" values="75; 250" dur="3.1s" repeatCount="indefinite" begin="1.8s" />
                <animate attributeName="cy" values="75; 250" dur="3.1s" repeatCount="indefinite" begin="1.8s" />
                <animate attributeName="opacity" values="1; 1; 0" keyTimes="0; 0.7; 1" dur="3.1s" repeatCount="indefinite" begin="1.8s" />
              </circle>

            </svg>

            {/* Nodo Central (O ERP com a Logo) */}
            <div className={styles.centerNode}>
              <Image 
                src="/assets/images/logo.png" 
                alt="Aesir Logo" 
                width={52} 
                height={52} 
                style={{ objectFit: 'contain' }}
              />
              <h3>Aesir ERP</h3>
              <span>Painel Central</span>
            </div>

            {/* Nodo 1 (Topo) */}
            <div className={`${styles.orbitNode} ${styles.node1}`}>
              <div className={styles.iconCircle}><ShoppingCart size={24} /></div>
              <div className={styles.nodeInfo}>
                <div className={styles.nodeTitle}>E-commerce</div>
                <div className={styles.nodeSub}>Integrado</div>
              </div>
            </div>

            {/* Nodo 2 (Topo Direita) */}
            <div className={`${styles.orbitNode} ${styles.node2}`}>
              <div className={styles.iconCircle}><MessageSquare size={24} /></div>
              <div className={styles.nodeInfo}>
                <div className={styles.nodeTitle}>WhatsApp</div>
                <div className={styles.nodeSub}>IA Nativa</div>
              </div>
            </div>

            {/* Nodo 3 (Direita) */}
            <div className={`${styles.orbitNode} ${styles.node3}`}>
              <div className={styles.iconCircle}><Store size={24} /></div>
              <div className={styles.nodeInfo}>
                <div className={styles.nodeTitle}>PDV</div>
                <div className={styles.nodeSub}>Frente de Caixa</div>
              </div>
            </div>

            {/* Nodo 4 (Baixo Direita) */}
            <div className={`${styles.orbitNode} ${styles.node4}`}>
              <div className={styles.iconCircle}><DollarSign size={24} /></div>
              <div className={styles.nodeInfo}>
                <div className={styles.nodeTitle}>Financeiro</div>
                <div className={styles.nodeSub}>Notas Fiscais</div>
              </div>
            </div>

            {/* Nodo 5 (Baixo) */}
            <div className={`${styles.orbitNode} ${styles.node5}`}>
              <div className={styles.iconCircle}><Users size={24} /></div>
              <div className={styles.nodeInfo}>
                <div className={styles.nodeTitle}>CRM</div>
                <div className={styles.nodeSub}>Funil de Vendas</div>
              </div>
            </div>

            {/* Nodo 6 (Baixo Esquerda) */}
            <div className={`${styles.orbitNode} ${styles.node6}`}>
              <div className={styles.iconCircle}><Warehouse size={24} /></div>
              <div className={styles.nodeInfo}>
                <div className={styles.nodeTitle}>Estoque</div>
                <div className={styles.nodeSub}>Multi-lojas</div>
              </div>
            </div>

            {/* Nodo 7 (Esquerda) */}
            <div className={`${styles.orbitNode} ${styles.node7}`}>
              <div className={styles.iconCircle}><Wrench size={24} /></div>
              <div className={styles.nodeInfo}>
                <div className={styles.nodeTitle}>Serviços</div>
                <div className={styles.nodeSub}>Ordens (OS)</div>
              </div>
            </div>

            {/* Nodo 8 (Topo Esquerda) */}
            <div className={`${styles.orbitNode} ${styles.node8}`}>
              <div className={styles.iconCircle}><Zap size={24} /></div>
              <div className={styles.nodeInfo}>
                <div className={styles.nodeTitle}>Automação</div>
                <div className={styles.nodeSub}>Workflows</div>
              </div>
            </div>

          </div>
        </div>

      </div>
    </section>
  );
}